<script setup lang="ts">
// Enterprise destructive-action confirmation dialog. Left-aligned,
// identity-aware, consequence-explicit. Matches the operational tone
// expected on a regulated-finance back-office tool: the operator reads
// who is being touched, what changes as a result, that the action is
// auditable, and only then commits.
//
// Backwards-compatible with the original simpler shape — when neither
// `identity` nor any of the consequence/impact arrays are passed, the
// dialog renders as a compact title + message + cta confirm, still in
// the new left-aligned style.

import { computed } from 'vue'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Button from '@uiKit/elements/Button.vue'
import type { ButtonColor } from '@uiKit/elements/Button.vue'

export type DeleteConfirmSeverity = 'danger' | 'warn' | 'info'

export interface DeleteConfirmIdentity {
  /** Display name shown in the identity header. */
  name: string
  /** Email shown under the name. Optional. */
  email?: string
  /** Role shown beside the email (e.g. "Super Admin"). Optional. */
  role?: string
  /** Pre-computed initials. Falls back to the first two letters of `name`. */
  initials?: string
}

interface Props {
  /** Controls dialog visibility. v-model compatible. */
  visible: boolean
  /** Heading shown at the top of the modal. */
  title?: string
  /** Single-line subtitle / description under the heading. */
  message?: string
  /** Show a pending state on the confirm button. */
  loading?: boolean
  /** Override the confirm-button label. Defaults to `Confirm`. */
  confirmLabel?: string
  /** Override the cancel-button label. Defaults to `Cancel`. */
  cancelLabel?: string
  /** Severity drives icon + button colour. Defaults to `danger`. */
  severity?: DeleteConfirmSeverity
  /**
   * Optional record identity. When provided, renders an avatar + name +
   * email + role block — reduces accidental wrong-record actions.
   */
  identity?: DeleteConfirmIdentity | null
  /**
   * "This action will:" bullets. Use for the primary consequence list
   * (e.g. "Immediately terminate active sessions").
   */
  consequences?: string[]
  /** Optional "Affected access" sub-section bullets. */
  affectedAccess?: string[]
  /** Optional "Security impact" sub-section bullets. */
  securityImpact?: string[]
  /** Append the "This action cannot be undone." emphasis line. */
  irreversible?: boolean
  /**
   * Override the audit footer note. Defaults to
   * "This action will be recorded in the audit log."
   */
  auditNote?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  severity: 'danger',
  identity: null,
  consequences: () => [],
  affectedAccess: () => [],
  securityImpact: () => [],
  irreversible: false,
  auditNote: 'This action will be recorded in the audit log.',
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

// Map the dialog's logical severity to the kit Button's `color`. The
// confirm button stays semantically tied to the action's severity
// (danger / warn / info) — never a generic primary purple.
const confirmColor = computed<ButtonColor>(() => {
  if (props.severity === 'warn') return 'warning'
  if (props.severity === 'info') return 'info'
  return 'danger'
})

// Pre-computed initials for the avatar pill — first letter of the first
// two name parts, capitalised. Falls back to "?" if name is blank.
const identityInitials = computed(() => {
  if (!props.identity) return ''
  if (props.identity.initials) return props.identity.initials
  return (
    props.identity.name
      .split(' ')
      .slice(0, 2)
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  )
})

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('update:visible', false)
  emit('cancel')
}
</script>

<template>
  <Dialog
    :model-value="visible"
    modal
    width="560px"
    :show-close-icon="false"
    :dismissable="!loading"
    class="confirm-dialog"
    @update:model-value="emit('update:visible', $event)"
  >
    <template #header>
      <Box
        flex
        align="center"
        justify="between"
        gap="3"
        class="confirm-dialog__header"
        :class="[`confirm-dialog__header--${severity}`]"
      >
        <Box flex align="center" gap="3">
          <Box
            flex
            align="center"
            justify="center"
            :class="['confirm-dialog__icon', `confirm-dialog__icon--${severity}`]"
          >
            <PiIcon
              :name="
                severity === 'danger'
                  ? 'exclamation-triangle'
                  : severity === 'warn'
                    ? 'exclamation-circle'
                    : 'info-circle'
              "
            />
          </Box>
          <Box flex direction="col" gap="1">
            <Text type="title-lg" weight="semibold">
              {{ title || 'Confirm action' }}
            </Text>
            <Text v-if="message" type="body" color="muted">{{ message }}</Text>
          </Box>
        </Box>
        <Button
          icon="times"
          variant="text"
          color="secondary"
          size="small"
          class="confirm-dialog__close"
          aria-label="Close"
          :disabled="loading"
          @click="handleCancel"
        />
      </Box>
    </template>

    <Box flex direction="col" gap="4" class="confirm-dialog__body">
      <!-- Identity block — operator confirms they're touching the right record -->
      <Box v-if="identity" flex align="center" gap="3" class="confirm-dialog__identity">
        <Box flex align="center" justify="center" class="confirm-dialog__avatar">
          <Text is="span" type="body" weight="bold" color="inverse">{{ identityInitials }}</Text>
        </Box>
        <Box flex direction="col" gap="1">
          <Text type="body" weight="semibold">{{ identity.name }}</Text>
          <Box flex align="center" gap="2" class="confirm-dialog__identity-meta">
            <Text v-if="identity.email" type="caption" color="muted">{{ identity.email }}</Text>
            <Text v-if="identity.email && identity.role" is="span" type="caption" color="muted"
              >·</Text
            >
            <Text v-if="identity.role" type="caption" color="muted">{{ identity.role }}</Text>
          </Box>
        </Box>
      </Box>

      <!-- Primary consequence list -->
      <Box v-if="consequences.length" flex direction="col" gap="2">
        <Text type="caption-sm" transform="uppercase" weight="semibold" color="muted"
          >This action will</Text
        >
        <Box is="ul" flex direction="col" gap="2" class="confirm-dialog__list">
          <Text
            v-for="(line, idx) in consequences"
            :key="`consequence-${idx}`"
            is="li"
            type="body-sm"
          >
            {{ line }}
          </Text>
        </Box>
      </Box>

      <!-- Affected access -->
      <Box v-if="affectedAccess.length" flex direction="col" gap="2">
        <Text type="caption-sm" transform="uppercase" weight="semibold" color="muted"
          >Affected access</Text
        >
        <Box is="ul" flex direction="col" gap="2" class="confirm-dialog__list">
          <Text v-for="(line, idx) in affectedAccess" :key="`access-${idx}`" is="li" type="body-sm">
            {{ line }}
          </Text>
        </Box>
      </Box>

      <!-- Security impact -->
      <Box v-if="securityImpact.length" flex direction="col" gap="2">
        <Text type="caption-sm" transform="uppercase" weight="semibold" color="muted"
          >Security impact</Text
        >
        <Box is="ul" flex direction="col" gap="2" class="confirm-dialog__list">
          <Text v-for="(line, idx) in securityImpact" :key="`impact-${idx}`" is="li" type="body-sm">
            {{ line }}
          </Text>
        </Box>
      </Box>

      <!-- Irreversible emphasis -->
      <Text
        v-if="irreversible"
        type="body-sm"
        weight="semibold"
        :color="severity === 'info' ? 'info' : severity === 'warn' ? 'warning' : 'danger'"
      >
        This action cannot be undone.
      </Text>
    </Box>

    <template #footer>
      <Box flex align="center" justify="between" gap="3" class="confirm-dialog__footer">
        <Box flex align="center" gap="2" class="confirm-dialog__audit">
          <template v-if="auditNote">
            <PiIcon name="history" class="confirm-dialog__audit-icon" />
            <Text type="caption" color="muted">{{ auditNote }}</Text>
          </template>
        </Box>
        <Box flex align="center" gap="2">
          <Button
            :label="cancelLabel || 'Cancel'"
            color="secondary"
            variant="outline"
            :disabled="loading"
            @click="handleCancel"
          />
          <Button
            :label="confirmLabel || 'Confirm'"
            :color="confirmColor"
            :loading="loading"
            @click="handleConfirm"
          />
        </Box>
      </Box>
    </template>
  </Dialog>
</template>

<style scoped>
/* ── Dialog chrome overrides ── */
.confirm-dialog :deep(.app-dialog__panel) {
  border-radius: var(--p-border-radius-lg);
  box-shadow:
    0 10px 30px rgba(15, 23, 42, 0.08),
    0 2px 8px rgba(15, 23, 42, 0.04);
}

.confirm-dialog :deep(.app-dialog__mask) {
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
}

.confirm-dialog :deep(.app-dialog__header) {
  padding: 1.25rem 1.5rem 0.75rem !important;
  border-bottom: 1px solid var(--p-content-border-color);
}

.confirm-dialog :deep(.app-dialog__content) {
  padding: 1.25rem 1.5rem !important;
}

.confirm-dialog :deep(.p-dialog-footer) {
  padding: 0 !important;
  border-top: none !important;
}

/* ── Header ── */
.confirm-dialog__header {
  width: 100%;
}

.confirm-dialog__icon {
  width: 36px;
  height: 36px;
  border-radius: var(--p-border-radius-md);
  flex-shrink: 0;
  font-size: 16px;
}

.confirm-dialog__icon--danger {
  background: var(--p-danger-50);
  color: var(--p-danger-700);
}

.confirm-dialog__icon--warn {
  background: var(--p-warning-50);
  color: var(--p-warning-700);
}

.confirm-dialog__icon--info {
  background: var(--p-info-50);
  color: var(--p-info-700);
}

.confirm-dialog__close {
  flex-shrink: 0;
}

/* ── Body ── */
.confirm-dialog__body {
  width: 100%;
}

.confirm-dialog__identity {
  padding: 0.875rem 1rem;
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  border: 1px solid var(--p-content-border-color);
}

.confirm-dialog__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--p-surface-900);
  flex-shrink: 0;
  letter-spacing: 0.04em;
}

.confirm-dialog__identity-meta {
  flex-wrap: wrap;
}

.confirm-dialog__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.confirm-dialog__list :deep(li) {
  position: relative;
  padding-left: 1rem;
}

.confirm-dialog__list :deep(li)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--p-text-muted-color);
}

/* ── Footer ── */
.confirm-dialog__footer {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-top: 1px solid var(--p-content-border-color);
  background: var(--p-surface-50);
  border-bottom-left-radius: var(--p-border-radius-lg);
  border-bottom-right-radius: var(--p-border-radius-lg);
  flex-wrap: wrap;
}

.confirm-dialog__audit {
  min-width: 0;
}

.confirm-dialog__audit-icon {
  font-size: 12px;
  color: var(--p-text-muted-color);
}
</style>
